import {
  Box,
  Fab,
  SwipeableDrawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  useTheme,
  alpha
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Laptop as LaptopIcon,
  Shield as ShieldIcon
} from '@mui/icons-material';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useIsAdmin } from '@stores/userStore';

type SettingsSection = {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  adminOnly?: boolean;
};

const settingsSections: SettingsSection[] = [
  {
    id: 'profile',
    label: 'User Profile',
    icon: <PersonIcon />,
    path: '/settings/profile'
  },
  {
    id: 'application',
    label: 'Application',
    icon: <LaptopIcon />,
    path: '/settings/application'
  },
  {
    id: 'administration',
    label: 'Administration',
    icon: <ShieldIcon />,
    path: '/settings/administration',
    adminOnly: true
  }
];

export const MobileNavigation = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const visibleSections = settingsSections.filter(
    section => !section.adminOnly || isAdmin
  );

  const handleSectionClick = (path: string) => {
    navigate(path);
    setDrawerOpen(false);
  };

  const toggleDrawer = (open: boolean) => {
    setDrawerOpen(open);
  };

  return (
    <>
      <Fab
        color="primary"
        aria-label="settings navigation"
        onClick={() => toggleDrawer(true)}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: theme.zIndex.speedDial,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          boxShadow: '0 8px 16px 0 rgba(0,0,0,0.12)',
          '&:hover': {
            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
            transform: 'scale(1.05)',
            boxShadow: '0 12px 20px 0 rgba(0,0,0,0.16)'
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        <MenuIcon />
      </Fab>

      <SwipeableDrawer
        anchor="bottom"
        open={drawerOpen}
        onClose={() => toggleDrawer(false)}
        onOpen={() => toggleDrawer(true)}
        disableSwipeToOpen={false}
        ModalProps={{
          keepMounted: true
        }}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            maxHeight: '50vh',
            background: theme.palette.background.paper,
            boxShadow: '0 -8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <Box
          sx={{
            p: 3,
            pb: `calc(24px + env(safe-area-inset-bottom))`
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 3
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                Settings
              </Typography>
            </Box>
            <IconButton
              onClick={() => toggleDrawer(false)}
              sx={{
                color: theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.action.hover, 0.8)
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box
            sx={{
              width: '40px',
              height: '4px',
              backgroundColor: alpha(theme.palette.text.secondary, 0.3),
              borderRadius: '2px',
              position: 'absolute',
              top: 12,
              left: '50%',
              transform: 'translateX(-50%)'
            }}
          />

          <List sx={{ py: 0 }}>
            {visibleSections.map((section, index) => {
              const isActive =
                location.pathname === section.path ||
                (section.id === 'administration' &&
                  location.pathname.startsWith('/settings/administration'));

              return (
                <ListItem key={section.id} disablePadding sx={{ mb: 1 }}>
                  <ListItemButton
                    onClick={() => handleSectionClick(section.path)}
                    sx={{
                      borderRadius: 3,
                      py: 2,
                      px: 3,
                      backgroundColor: isActive
                        ? alpha(theme.palette.primary.main, 0.12)
                        : 'transparent',
                      border: isActive
                        ? `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                        : '1px solid transparent',
                      color: isActive
                        ? theme.palette.primary.main
                        : theme.palette.text.primary,
                      '&:hover': {
                        backgroundColor: isActive
                          ? alpha(theme.palette.primary.main, 0.16)
                          : alpha(theme.palette.action.hover, 0.8),
                        transform: 'translateY(-1px)',
                        boxShadow: isActive
                          ? `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                          : '0 2px 8px rgba(0,0,0,0.08)'
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      animation: `slideInUp 0.3s ease-out ${index * 0.1}s both`
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: 'inherit',
                        minWidth: 48,
                        '& svg': {
                          fontSize: 24
                        }
                      }}
                    >
                      {section.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={section.label}
                      sx={{
                        '& .MuiListItemText-primary': {
                          fontWeight: isActive ? 600 : 500,
                          fontSize: '1rem'
                        }
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>

          {!isAdmin && (
            <Box
              sx={{
                mt: 3,
                p: 2,
                backgroundColor: alpha(theme.palette.warning.main, 0.08),
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.warning.dark,
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <ShieldIcon sx={{ fontSize: 16 }} />
                Admin features require special permissions
              </Typography>
            </Box>
          )}
        </Box>
      </SwipeableDrawer>

      <style>
        {`
          @keyframes slideInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </>
  );
};
